import type { Guest, SeatingTable } from '../types';
import { buildUnits } from './companions';

export interface AutoSeatOptions {
  skipHeadTable: boolean;
  excludeIsCouple: boolean;
}

export interface AutoSeatResult {
  plan: Map<string, string[]>;
  unplaced: string[];
}

function isAdult(g: Guest): boolean {
  return !g.age_group || g.age_group === 'adult';
}

function isChild(g: Guest): boolean {
  return g.age_group === 'child' || g.age_group === 'baby';
}

export function autoSeat(guests: Guest[], tables: SeatingTable[], options: AutoSeatOptions): AutoSeatResult {
  const plan = new Map<string, string[]>();
  const unplaced: string[] = [];

  const seatedAt = new Map<string, Guest[]>();
  for (const t of tables) seatedAt.set(t.id, []);
  for (const g of guests) {
    if (g.table_id && seatedAt.has(g.table_id)) seatedAt.get(g.table_id)!.push(g);
  }
  const plannedAt = new Map<string, Guest[]>(tables.map((t) => [t.id, []]));

  const eligible = tables
    .filter((t) => !(options.skipHeadTable && t.type === 'head'))
    .slice()
    .sort((a, b) => (a.id < b.id ? -1 : a.id > b.id ? 1 : 0));

  const remaining = (t: SeatingTable): number =>
    t.seats - (seatedAt.get(t.id) || []).length - (plannedAt.get(t.id) || []).length;

  const groupHasSeated = (t: SeatingTable, groupId: string): boolean =>
    (seatedAt.get(t.id) || []).some((g) => (g.group_id || g.id) === groupId);

  const groupHasPlanned = (t: SeatingTable, groupId: string): boolean =>
    (plannedAt.get(t.id) || []).some((g) => (g.group_id || g.id) === groupId);

  const tableHasAdult = (t: SeatingTable): boolean =>
    [...(seatedAt.get(t.id) || []), ...(plannedAt.get(t.id) || [])].some(isAdult);

  function canPlace(t: SeatingTable, unit: Guest[]): boolean {
    if (unit.length > remaining(t)) return false;
    const hasAdultInUnit = unit.some(isAdult);
    if (!hasAdultInUnit && unit.some(isChild)) return tableHasAdult(t);
    return true;
  }

  function place(t: SeatingTable, unit: Guest[]): void {
    const list = plan.get(t.id) || [];
    plan.set(t.id, [...list, ...unit.map((g) => g.id)]);
    plannedAt.get(t.id)!.push(...unit);
  }

  // Candidates: only unseated guests (optionally without is_couple).
  const candidates = guests
    .filter((g) => !g.table_id)
    .filter((g) => !(options.excludeIsCouple && g.is_couple))
    .sort((a, b) => (a.id < b.id ? -1 : a.id > b.id ? 1 : 0));

  const units = buildUnits(candidates);

  // Group units by group_id; ungrouped units are their own groups.
  const groups = new Map<string, Guest[][]>();
  for (const unit of units) {
    const key = unit[0].group_id || unit[0].id;
    const list = groups.get(key) || [];
    list.push(unit);
    groups.set(key, list);
  }
  const orderedGroups = [...groups.entries()]
    .map(([key, list]) => ({ key, list, size: list.reduce((s, u) => s + u.length, 0) }))
    .sort((a, b) => b.size - a.size || (a.key < b.key ? -1 : a.key > b.key ? 1 : 0));

  function placeUnit(unit: Guest[], groupKey: string): void {
    const fitting = eligible.filter((t) => canPlace(t, unit));
    if (fitting.length === 0) {
      unplaced.push(...unit.map((g) => g.id));
      return;
    }
    fitting.sort((a, b) => {
      const ownA = groupHasSeated(a, groupKey) || groupHasPlanned(a, groupKey) ? 0 : 1;
      const ownB = groupHasSeated(b, groupKey) || groupHasPlanned(b, groupKey) ? 0 : 1;
      return ownA - ownB || remaining(b) - remaining(a) || (a.id < b.id ? -1 : 1);
    });
    place(fitting[0], unit);
  }

  for (const group of orderedGroups) {
    const groupGuests = group.list.flat();
    const fitsWhole = (t: SeatingTable) => canPlace(t, groupGuests);

    // (a) a table where this group already sits, if the whole group fits
    const ownTables = eligible.filter((t) => (groupHasSeated(t, group.key) || groupHasPlanned(t, group.key)) && fitsWhole(t));
    if (ownTables.length > 0) {
      ownTables.sort((a, b) => remaining(a) - remaining(b) || (a.id < b.id ? -1 : 1));
      place(ownTables[0], groupGuests);
      continue;
    }

    // (b) best fit: tightest table that fits the whole group
    const fitting = eligible.filter(fitsWhole);
    if (fitting.length > 0) {
      fitting.sort((a, b) => remaining(a) - remaining(b) || (a.id < b.id ? -1 : 1));
      place(fitting[0], groupGuests);
      continue;
    }

    // (c) split by units: own tables first, then most free seats
    for (const unit of group.list) {
      placeUnit(unit, group.key);
    }
  }

  return { plan, unplaced };
}
